import clsx from 'clsx';
import React, { useState, useRef } from 'react';
import ReactDOM from 'react-dom';

import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';

import { TagType } from '../types';
import { isHexColorDark } from '../utils';

export interface TagPillProps {
  tag: TagType;
  onClick?: (e: React.MouseEvent) => void;
  onRemove?: () => void; // X button (unassign)
  onDelete?: () => void; // Delete menu (delete from project)
  onEdit?: () => void;
  removable?: boolean;
  className?: string;
  style?: React.CSSProperties;
  color?: string;
  isTagReadOnly?: boolean;
  isFromSelectionsGrid?: boolean;
}
const TagPill = React.forwardRef<HTMLButtonElement, TagPillProps>(
  (
    {
      tag,
      className,
      style,
      onRemove,
      onDelete,
      onEdit,
      removable = false,
      onClick,
      isTagReadOnly = false,
      isFromSelectionsGrid,
    },
    ref
  ) => {
    const [hovered, setHovered] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
    const [showSystemTooltip, setShowSystemTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(
      null
    );
    // Use local ref for portal positioning if ref is not a ref object
    const localRef = useRef<HTMLButtonElement>(null);
    const pillRef = ref && typeof ref !== 'function' ? ref : localRef;
    const isSystem = tag['is-system'];

    const pillClassName = clsx(
      'tw-relative',
      'tw-px-3',
      'tw-py-1',
      'tw-rounded-full',
      'tw-text-sm',
      'tw-flex',
      'tw-items-center',
      'tw-gap-1',
      'tw-min-w-[60px]',
      'tw-max-w-[90px]',
      className,
      tag.color
    );

    const pillStyle = {
      ...style,
      backgroundColor: tag.color ? tag.color : undefined,
      color: isHexColorDark(tag.color) === 'dark' ? 'white' : 'black',
      borderWidth: '0.5px',
      borderColor: tag.color === '#ffffff' ? '#d1d5db' : 'inherit',
      borderStyle: 'solid',
      marginRight: 0,
      cursor: hovered ? 'pointer' : 'default',
    };
    return (
      <button
        ref={pillRef}
        type="button"
        className={pillClassName}
        style={pillStyle}
        onClick={(e) => {
          // Only trigger onClick if clicking the main pill area
          if (e.target === e.currentTarget || (e.target as HTMLElement).closest('.tw-truncate')) {
            onClick?.(e);
          }
        }}
        onMouseEnter={() => {
          setHovered(true);
        }}
        onMouseLeave={() => {
          setHovered(false);
          setMenuOpen(false);
        }}
        title={tag.name}
      >
        <span className="tw-truncate tw-block" style={{ maxWidth: 90 }}>
          {tag.name}
        </span>
        {removable && (
          <span
            className="tw-absolute tw-right-2 tw-flex tw-items-center tw-h-full"
            style={{ top: 0, bottom: 0, right: 0, margin: 'auto' }}
          >
            {(hovered || menuOpen) && !isTagReadOnly ? (
              <span
                className={clsx(
                  'tw-bg-cfo-black',
                  'tw-rounded-full',
                  'tw-flex',
                  'tw-items-center',
                  'tw-gap-1',
                  'tw-px-2',
                  'tw-py-1'
                )}
                style={{
                  minWidth: 38,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {!isFromSelectionsGrid && (
                  <button
                    className={clsx(
                      'tw-text-xs',
                      'tw-text-white',
                      'tw-bg-transparent',
                      'tw-p-0',
                      'tw-m-0'
                    )}
                    style={{
                      width: 12,
                      height: 12,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (onRemove) onRemove();
                    }}
                    aria-label="Unassign tag"
                  >
                    <Icon icon={ICONS.close} className="tw-text-[12px]" />
                  </button>
                )}
                {isSystem ? (
                  <button
                    disabled={false}
                    id="system-tag-options-btn"
                    className={clsx(
                      'tw-text-xs',
                      'tw-text-white',
                      'tw-bg-transparent',
                      'tw-p-0',
                      'tw-m-0'
                    )}
                    style={{
                      width: 12,
                      height: 12,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      opacity: 0.5,
                      pointerEvents: 'auto',
                    }}
                    aria-label="Tag options disabled"
                    onMouseEnter={() => {
                      setShowSystemTooltip(true);
                      if (pillRef && pillRef.current) {
                        const rect = pillRef.current.getBoundingClientRect();
                        setTooltipPosition({
                          top: rect.top - 36,
                          left: rect.left + rect.width / 2,
                        });
                      }
                    }}
                    onMouseLeave={() => {
                      setShowSystemTooltip(false);
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowSystemTooltip(true);
                      if (pillRef && pillRef.current) {
                        const rect = pillRef.current.getBoundingClientRect();
                        setTooltipPosition({
                          top: rect.top - 36,
                          left: rect.left + rect.width / 2,
                        });
                      }
                      setTimeout(() => setShowSystemTooltip(false), 1500);
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: 12,
                        alignSelf: 'start',
                        position: 'absolute',
                        height: 24,
                        top: -2,
                      }}
                    >
                      ...
                    </span>
                  </button>
                ) : (
                  <button
                    className={clsx(
                      'tw-text-xs',
                      'tw-text-white',
                      'tw-bg-transparent',
                      'tw-p-0',
                      'tw-m-0'
                    )}
                    style={{
                      width: 12,
                      height: 12,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      opacity: tag.isSystem ? 0.5 : 1,
                      pointerEvents: 'auto',
                    }}
                    onClick={(e) => {
                      if (tag.isSystem) {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowSystemTooltip(true);
                        if (pillRef && pillRef.current) {
                          const rect = pillRef.current.getBoundingClientRect();
                          setTooltipPosition({
                            top: rect.top - 36,
                            left: rect.left + rect.width / 2,
                          });
                        }
                        setTimeout(() => setShowSystemTooltip(false), 1500);
                        return;
                      }
                      e.preventDefault();
                      e.stopPropagation();
                      if (pillRef && pillRef.current) {
                        const rect = pillRef.current.getBoundingClientRect();
                        setMenuPosition({
                          top: rect.bottom + window.scrollY,
                          left: rect.left + window.scrollX + rect.width / 2,
                        });
                      }
                      setMenuOpen(true);
                    }}
                    aria-label="Tag options"
                    disabled={false}
                    onMouseEnter={(e) => {
                      if (tag.isSystem || isSystem) {
                        setShowSystemTooltip(true);
                        if (pillRef && pillRef.current) {
                          const rect = pillRef.current.getBoundingClientRect();
                          setTooltipPosition({
                            top: rect.top - 36,
                            left: rect.left + rect.width / 2,
                          });
                        }
                      }
                    }}
                    onMouseLeave={() => {
                      if (tag.isSystem || isSystem) {
                        setShowSystemTooltip(false);
                      }
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: 12,
                        alignSelf: 'start',
                        position: 'absolute',
                        height: 24,
                        top: -2,
                      }}
                    >
                      ...
                    </span>
                    {tag.isSystem &&
                      showSystemTooltip &&
                      tooltipPosition &&
                      ReactDOM.createPortal(
                        <SystemTagTooltip
                          top={tooltipPosition.top + 45}
                          left={tooltipPosition.left + 36}
                        />,
                        document.body
                      )}
                  </button>
                )}
                {isSystem &&
                  showSystemTooltip &&
                  tooltipPosition &&
                  ReactDOM.createPortal(
                    <SystemTagTooltip
                      top={tooltipPosition.top + 45}
                      left={tooltipPosition.left + 36}
                    />,
                    document.body
                  )}
              </span>
            ) : null}
          </span>
        )}
        {removable &&
          menuOpen &&
          menuPosition &&
          typeof window !== 'undefined' &&
          ReactDOM.createPortal(
            <div
              className={clsx(
                'tw-absolute',
                'tw-bg-white',
                'tw-shadow-lg',
                'tw-rounded',
                'tw-z-[999]',
                'tw-flex',
                'tw-flex-col'
              )}
              style={{
                top: menuPosition.top,
                left: menuPosition.left,
                transform: 'translateX(-50%)',
                width: '150px',
                position: 'absolute',
              }}
            >
              <button
                className={clsx(
                  'tw-text-sm',
                  'tw-text-gray-600',
                  'hover:tw-text-green-600',
                  'tw-p-4',
                  'tw-text-left',
                  'hover:tw-bg-cfo-blue-200'
                )}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setMenuOpen(false);
                  if (onEdit) onEdit();
                }}
                aria-label="Edit tag"
              >
                <Icon icon={ICONS.edit} className="tw-text-[16px] tw-mr-1" /> Edit
              </button>
              <button
                className={clsx(
                  'tw-text-sm',
                  'tw-text-gray-600',
                  'hover:tw-text-blue-600',
                  'tw-text-left',
                  'hover:tw-bg-cfo-blue-200',
                  'tw-p-4'
                )}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setMenuOpen(false);
                  onDelete();
                  if (onDelete) {
                    onDelete();
                  }
                }}
                aria-label="Delete tag"
              >
                <Icon icon={ICONS.delete} className="tw-text-[16px] tw-mr-1 tw-text-red-500" />{' '}
                Delete
              </button>
            </div>,
            document.body
          )}
      </button>
    );
  }
);

TagPill.displayName = 'TagPill';

// Reusable system tag tooltip component
const SystemTagTooltip = ({
  top,
  left,
  text = 'System generated',
}: {
  top: number;
  left: number;
  text?: string;
}) => {
  return (
    <div
      style={{
        position: 'fixed',
        top: top,
        left: left,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {/* Floating icon, visually separated, left of tooltip */}
      <div
        style={{
          borderRadius: '50%',
          width: 32,
          height: 32,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginRight: 4,
          zIndex: 10000,
        }}
      >
        <Icon
          icon={ICONS.do_not_disturb}
          className="tw-text-[24px] tw-font-bold tw-text-cfo-grey-600"
        />
      </div>
      {/* Main tooltip box */}
      <div
        className={'tw-bg-cfo-black tw-text-cfo-white '}
        style={{
          color: '#fff',
          fontSize: 15,
          paddingRight: 12,
          borderRadius: 4,
          whiteSpace: 'nowrap',
          display: 'flex',
          alignItems: 'center',
          minWidth: 140,
        }}
      >
        <span
          style={{
            borderRadius: '50%',
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
            marginRight: 10,
          }}
        >
          <Icon icon={ICONS.error_outline} className="tw-text-[18px] tw-text-white" />
        </span>
        <span>{text}</span>
      </div>
    </div>
  );
};

export default TagPill;
